import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState } from "@redotech/react-util/load";
import { useSearch } from "@redotech/react-util/search";
import {
  FulfillmentGroupTagSource,
  Tag,
  TagKind,
} from "@redotech/redo-common-model-tags-common/tag";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { CreateTagModal } from "@redotech/redo-web/tags/create-tag-modal";
import {
  BadgePillThemeToColorMapping,
  BadgePillThemeToTypeMapping,
} from "@redotech/redo-web/tags/tag-pill";
import { unique } from "@redotech/util/array";
import Fuse from "fuse.js";
import { useId, useMemo, useState } from "react";
import { TagList } from "../../common/tag-list";
import { useFulfillmentGroupPermissions } from "../../utils/use-assigned-fulfillment-service";
import * as tagsCellCss from "./tags-cell.module.css";

export const Tags = ({
  data,
  updateData,
  availableTagsLoad,
  getAvailableTags,
  disableOverflow = false,
  alwaysShowAddTagButton = false,
}: {
  data: FulfillmentGroupData;
  updateData: (updates: FulfillmentGroupUpdate[]) => void;
  availableTagsLoad: LoadState<Tag[] | undefined>;
  getAvailableTags: (signal?: AbortSignal) => void;
  disableOverflow?: boolean;
  alwaysShowAddTagButton?: boolean;
}) => {
  const { permissionsToEditEveryGroup } = useFulfillmentGroupPermissions([
    data,
  ]);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const [tagBeingDeleted, setTagBeingDeleted] = useState<Tag | undefined>();
  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);
  const currentTags = useMemo(
    () => (data.tags ? [...data.tags] : []),
    [data.tags],
  );

  const [selectDropdownOpen, setSelectDropdownOpen] = useState(false);

  const availableTags = useMemo(
    () => availableTagsLoad.value ?? [],
    [availableTagsLoad],
  );

  function removeTagFromFulfillmentGroup(tag: Tag) {
    const newTags = currentTags.filter((current) => current.name !== tag.name);
    updateData([{ tags: newTags, fulfillmentGroupId: data._id }]);
  }

  function addTagToFulfillmentGroup(tag: Tag) {
    const newTags = [...currentTags, tag];
    updateData([{ tags: newTags, fulfillmentGroupId: data._id }]);
  }

  const addNewTagButton = {
    name: "Add Redo shipment tag",
    pillTheme: PillTheme.NORMAL,
    source: FulfillmentGroupTagSource.MERCHANT,
    kind: TagKind.FULFILLMENT_GROUP,
  };

  const existingTags = (
    <Flex
      cursor={permissionsToEditEveryGroup ? "pointer" : "default"}
      onClick={(e) => {
        e.stopPropagation();
        setSelectDropdownOpen(!selectDropdownOpen);
      }}
      ref={setDropdownAnchor}
      w="full"
    >
      <TagList
        compactFallback={!disableOverflow}
        disableOverflow={disableOverflow}
        tags={
          currentTags.length > 0
            ? [
                ...currentTags,
                ...(alwaysShowAddTagButton ? [addNewTagButton] : []),
              ]
            : [addNewTagButton]
        }
      />
    </Flex>
  );

  const tagOptions = useMemo(() => {
    const uniqueTags = unique(
      [...(data.tags ?? []), ...availableTags],
      (tag) => tag.name,
    );
    const selectedTags = new Set(currentTags.map((tag) => tag.name));

    return uniqueTags.map<RedoListItem<Tag>>((tag) => ({
      type: "badge",
      badge: {
        text: tag.name,
        color: BadgePillThemeToColorMapping[tag.pillTheme],
        type: BadgePillThemeToTypeMapping[tag.pillTheme],
      },
      id: tag.name,
      menu: selectedTags.has(tag.name)
        ? undefined
        : { onClick: () => setTagBeingDeleted(tag) },
      value: tag,
    }));
  }, [availableTags, data.tags, currentTags]);

  const selectedTagOptions = useMemo(() => {
    const selectedTags = new Set(currentTags.map((tag) => tag.name));

    return tagOptions.filter((tag) => selectedTags.has(tag.value.name));
  }, [currentTags, tagOptions]);

  const [search, setSearch] = useState("");

  const tagSearcher = useMemo(() => {
    return new Fuse<RedoListItem<Tag>>([], {
      keys: ["value.name", "badge.text"],
      threshold: 0.3,
    });
  }, []);

  const searchedTags = useSearch(tagSearcher, tagOptions, search);

  const id = useId();

  const staticItems = useMemo(() => {
    const createTagItem: RedoListItem<void> = {
      id: `create-tag-${id}`,
      type: "text",
      text: "Create new tag",
      value: undefined,
      leadingItem: { type: "icon", Icon: PlusIcon },
    };
    return [createTagItem];
  }, [id]);

  const staticItemSelected = useHandler((item: RedoListItem<void>) => {
    if (item.id === `create-tag-${id}`) {
      setSelectDropdownOpen(false);
      setCreateTagModalOpen(true);
    }
  });

  const dropdownItems = (
    <RedoMultiselectDropdown<Tag>
      dropdownAnchor={dropdownAnchor}
      dropdownOpen={selectDropdownOpen}
      items={searchedTags}
      searchPlaceholder="Search Redo shipment tags"
      searchString={search}
      selectedItems={selectedTagOptions}
      setDropdownOpen={setSelectDropdownOpen}
      setSearchString={setSearch}
      setSelectedItems={(items) => {
        const tags = items.map((item) => item.value);
        updateData([{ tags, fulfillmentGroupId: data._id }]);
      }}
      staticItems={staticItems}
      staticItemSelected={staticItemSelected}
    />
  );

  const handleSubmit = useHandler(
    async ({ name, pillTheme }: { name: string; pillTheme: PillTheme }) => {
      setSelectDropdownOpen(true);
      setCreateTagModalOpen(false);

      const tagWithNumericId = await rpcClient.upsertTag({
        tag: {
          name,
          kind: TagKind.FULFILLMENT_GROUP,
          pillTheme,
          source: FulfillmentGroupTagSource.MERCHANT,
        },
      });

      addTagToFulfillmentGroup(tagWithNumericId);
      getAvailableTags();
    },
  );

  const handleDelete = useHandler(async (tag: Tag) => {
    removeTagFromFulfillmentGroup(tag);
    await rpcClient.deleteTag({ name: tag.name, kind: tag.kind });
    getAvailableTags();
  });

  const deleteModal = tagBeingDeleted && (
    <Flex onClick={(e) => e.stopPropagation()}>
      <RedoModal
        modalSize="sm"
        muiClassName={tagsCellCss.deleteModal}
        onModalCloseRequested={() => {
          setTagBeingDeleted(undefined);
        }}
        primaryButton={{
          text: "Delete",
          onClick: async () => {
            await handleDelete(tagBeingDeleted);
            setTagBeingDeleted(undefined);
          },
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setTagBeingDeleted(undefined);
          },
        }}
        subtitle="Tag will not be removed from current orders"
        title="Remove tag"
      />
    </Flex>
  );

  const createModal = createTagModalOpen && (
    <div onClick={(e) => e.stopPropagation()}>
      <CreateTagModal
        cancelClicked={() => {
          setSelectDropdownOpen(true);
          setCreateTagModalOpen(false);
        }}
        handleCreateTag={handleSubmit}
      />
    </div>
  );

  return (
    <Flex w="full">
      {existingTags}
      {permissionsToEditEveryGroup && dropdownItems}
      {createModal}
      {deleteModal}
    </Flex>
  );
};

export const TagsCell = ({
  rowData,
  updateRowData,
  availableTagsLoad,
  getAvailableTags,
}: {
  rowData: FulfillmentGroupData;
  updateRowData: (rowData: FulfillmentGroupUpdate[]) => void;
  availableTagsLoad: LoadState<Tag[] | undefined>;
  getAvailableTags: (signal?: AbortSignal) => void;
}) => {
  return (
    <Flex pl="lg">
      <Tags
        availableTagsLoad={availableTagsLoad}
        data={rowData}
        getAvailableTags={getAvailableTags}
        updateData={updateRowData}
      />
    </Flex>
  );
};
